import moment from 'moment';
import { outageLength } from './meterAlerts';
import { readingStateLabel } from './meterFilters';

/**
 * Which meters were down over a period, for how long, and why.
 *
 * The meters list says what is wrong now. It cannot answer "what has this
 * fleet been doing for a month", which is the question asked when a total
 * looks low or a report is due.
 *
 * Everything here is derived from alert instances the status job already
 * raises for the scheduled email, so the report costs one request and no new
 * storage. That also bounds what it can see: an outage only exists here if an
 * enabled alert rule covered the meter and its escalation threshold passed.
 * `coverageNote` exists so the modal can say that out loud rather than let a
 * short list read as a quiet month.
 */

/** Periods the report offers, and how far back each looks. */
export const REPORT_PERIODS = [
  { id: 'week', label: 'Last 7 days', days: 7 },
  { id: 'month', label: 'Last 30 days', days: 30 },
  { id: 'quarter', label: 'Last 90 days', days: 90 },
];

/**
 * The window a period covers, ending now.
 * @param {string} periodId - One of REPORT_PERIODS.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {{from: object, to: object}} Moment bounds.
 */
export const reportWindow = (periodId, now = () => moment()) => {
  const period = REPORT_PERIODS.find((p) => p.id === periodId) || REPORT_PERIODS[1];
  const to = now();

  return { from: to.clone().subtract(period.days, 'days'), to };
};

/**
 * Whether an outage overlaps the window at all.
 *
 * An outage that began before the window and is still running belongs in it —
 * it is the most severe kind, and filtering on `openedAt` alone would drop the
 * one the reader most needs. A closed outage counts when it ended inside the
 * window even if it started before.
 *
 * @param {object} instance - Alert instance from the API.
 * @param {{from: object, to: object}} window - Bounds from `reportWindow`.
 * @returns {boolean} True when any part of it falls inside.
 */
const overlaps = (instance, window) => {
  const opened = moment(instance.openedAt);
  if (!opened.isValid()) return false;

  const closed = instance.closedAt ? moment(instance.closedAt) : null;
  if (closed && closed.isBefore(window.from)) return false;

  return opened.isSameOrBefore(window.to);
};

/**
 * Hours an outage ran for, clipped to the window so a month's total cannot
 * exceed the month.
 *
 * @param {object} instance - Alert instance from the API.
 * @param {{from: object, to: object}} window - Bounds from `reportWindow`.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {number} Hours inside the window.
 */
const hoursWithin = (instance, window, now = () => moment()) => {
  const opened = moment.max(moment(instance.openedAt), window.from);
  const closed = instance.closedAt ? moment(instance.closedAt) : now();

  return Math.max(0, moment.min(closed, window.to).diff(opened, 'hours', true));
};

/**
 * Builds the rows of the downtime report.
 *
 * @param {Array<object>} instances - Alert instances across the fleet.
 * @param {Array<object>} meters - Meters, for names and when each last spoke.
 * @param {string} periodId - One of REPORT_PERIODS.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {{rows: Array<object>, meters: number, outages: number, hours: number}}
 *   One row per outage, newest first, with the totals for the heading.
 */
export const buildDowntimeReport = (
  instances = [],
  meters = [],
  periodId = 'month',
  now = () => moment()
) => {
  const window = reportWindow(periodId, now);
  const byId = new Map(
    (Array.isArray(meters) ? meters : []).map((m) => [m.meterId, m])
  );

  const rows = (Array.isArray(instances) ? instances : [])
    .filter((instance) => instance?.openedAt && overlaps(instance, window))
    .map((instance) => {
      const meter = byId.get(instance.meterId);

      return {
        id: instance.id,
        meterId: instance.meterId,
        meterName: meter?.meterName || instance.meterId,
        openedAt: instance.openedAt,
        started: moment(instance.openedAt).format('DD MMM, HH:mm'),
        ended: instance.closedAt
          ? moment(instance.closedAt).format('DD MMM, HH:mm')
          : null,
        open: !instance.closedAt,
        length: outageLength(instance.openedAt, instance.closedAt, now),
        hours: hoursWithin(instance, window, now),
        fault: readingStateLabel(instance.meterReadingState),
        lastSeenAt: meter?.lastSeenAt
          ? moment(meter.lastSeenAt).format('DD MMM, HH:mm')
          : null,
      };
    })
    .sort((a, b) => new Date(b.openedAt) - new Date(a.openedAt));

  return {
    rows,
    meters: new Set(rows.map((row) => row.meterId)).size,
    outages: rows.length,
    hours: rows.reduce((total, row) => total + row.hours, 0),
  };
};

/** Nothing chosen, which is every row. */
export const NO_FILTERS = { meterId: '', fault: '', openOnly: false };

/**
 * Whether a reader has narrowed the report at all.
 * @param {object} filters - The current selection.
 * @returns {boolean} True when at least one filter is set.
 */
export const isFiltered = (filters = NO_FILTERS) =>
  Boolean(filters.meterId || filters.fault || filters.openOnly);

/**
 * The meters this report has something to say about.
 *
 * Offered from the report rather than from the fleet: a dropdown of two
 * hundred meters, of which four went down, is a list to search rather than a
 * filter to use.
 *
 * @param {object} report - Output of `buildDowntimeReport`.
 * @returns {Array<{id: string, name: string}>} Meters, by name.
 */
export const metersInReport = (report) => {
  const byId = new Map();

  (report?.rows || []).forEach((row) => byId.set(row.meterId, row.meterName));

  return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
};

/**
 * The faults this report actually contains.
 * @param {object} report - Output of `buildDowntimeReport`.
 * @returns {Array<string>} Fault names, alphabetically.
 */
export const faultsInReport = (report) =>
  Array.from(new Set((report?.rows || []).map((row) => row.fault))).sort();

/**
 * Narrows a report, and recounts what is left.
 *
 * The totals are rebuilt rather than carried over, because the sentence above
 * the table is drawn from them: a filtered table under an unfiltered heading
 * reads as a report of a different fleet.
 *
 * @param {object} report - Output of `buildDowntimeReport`.
 * @param {object} filters - Meter, fault, and whether to keep only what is
 *   still down.
 * @returns {object} A report of the same shape, holding only the rows kept.
 */
export const filterDowntime = (report, filters = NO_FILTERS) => {
  const rows = (report?.rows || []).filter(
    (row) =>
      (!filters.meterId || row.meterId === filters.meterId) &&
      (!filters.fault || row.fault === filters.fault) &&
      (!filters.openOnly || row.open)
  );

  return {
    rows,
    meters: new Set(rows.map((row) => row.meterId)).size,
    outages: rows.length,
    hours: rows.reduce((total, row) => total + row.hours, 0),
  };
};

/**
 * States what the report found, for the heading.
 * @param {object} report - Output of `buildDowntimeReport`.
 * @param {string} periodId - One of REPORT_PERIODS.
 * @returns {string} A sentence.
 */
export const describeDowntime = (report, periodId = 'month') => {
  const period = REPORT_PERIODS.find((p) => p.id === periodId) || REPORT_PERIODS[1];
  const window = period.label.toLowerCase();

  if (!report?.outages) return `No meter went down in the ${window}.`;

  const meters = `${report.meters} meter${report.meters === 1 ? '' : 's'}`;
  const outages = `${report.outages} outage${report.outages === 1 ? '' : 's'}`;
  const days = Math.round((report.hours / 24) * 10) / 10;
  const down = `${days} day${days === 1 ? '' : 's'}`;

  return `${meters} had ${outages} in the ${window}, totalling ${down} down.`;
};

/**
 * Names how much of the fleet the report can actually see.
 *
 * An outage reaches this report only when an enabled alert rule covered the
 * meter and its threshold passed. A meter nobody wrote a rule for can be dark
 * for a month and never appear, and a short list would read as a quiet month
 * rather than as a gap in the watching.
 *
 * @param {Array<object>} meters - Every meter on the page.
 * @param {Array<object>} instances - Alert instances across the fleet.
 * @returns {string|null} A caveat, or null when every meter has been watched.
 */
export const coverageNote = (meters = [], instances = []) => {
  const total = (Array.isArray(meters) ? meters : []).length;
  if (!total) return null;

  const watched = new Set(
    (Array.isArray(instances) ? instances : []).map((i) => i.meterId)
  );
  const unwatched = meters.filter((m) => !watched.has(m.meterId)).length;

  if (!unwatched) return null;

  return `${unwatched} of ${total} meters have never raised an alert. A meter no alert rule covers cannot appear here, however long it is down.`;
};

/** Columns of the exported report, in the order a reader scans them. */
export const DOWNTIME_COLUMNS = [
  'Meter',
  'Meter ID',
  'Started',
  'Ended',
  'Down for',
  'Fault',
  'Last reported',
];

/**
 * Turns the report into rows for `toCsv`.
 *
 * An outage still running exports as "still down" rather than as a blank end
 * date, which a spreadsheet would read as an outage of no length.
 *
 * @param {object} report - Output of `buildDowntimeReport`.
 * @returns {Array<Array<string>>} Header row followed by one row per outage.
 */
export const toDowntimeRows = (report) => [
  DOWNTIME_COLUMNS,
  ...(report?.rows || []).map((row) => [
    row.meterName,
    row.meterId,
    row.started,
    row.open ? 'still down' : row.ended,
    row.length,
    row.fault,
    row.lastSeenAt || '',
  ]),
];
